import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { IconField } from 'primeng/iconfield';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputIcon } from 'primeng/inputicon';
import { InputNumber } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { MultiSelect } from 'primeng/multiselect';
import { Select } from 'primeng/select';
import Detail from '../../../../../../types/detail/Detail';
import Language from '../../../../../../types/language/Language';
import Manufacturer from '../../../../../../types/manufacturer/Manufacturer';
import Media from '../../../../../../types/media/Media';
import SampleCategory from '../../../../../../types/sample-category/SampleCategory';
import SampleLanguageWriteDTO from '../../../../../../types/sample-language/SampleLanguageWriteDTO';
import SampleType from '../../../../../../types/sample-type/SampleType';
import SampleBranding from '../../../../../../types/sample-vehicle-model/SampleBranding';
import { FieldOfUse } from '../../../../../../types/sample/FieldOfUse';
import SampleFormat from '../../../../../../types/sample/SampleFormat';
import SampleWriteDTO from '../../../../../../types/sample/SampleWriteDTO';
import SelectOption from '../../../../../../types/shared/SelectOption';
import { DetailService } from '../../../../services/detail.service';
import { LanguageService } from '../../../../services/language.service';
import { ManufacturerService } from '../../../../services/manufacturer.service';
import { SampleCategoryService } from '../../../../services/sample-category.service';
import { SampleTypeService } from '../../../../services/sample-type.service';
import { SampleService } from '../../../../services/sample.service';
import { UtilsService } from '../../../../services/utils.service';
import {
    SelectOrUploadMediaComponent
} from '../../media/select-or-upload-media-dialog/select-or-upload-media.component';

@Component({
    selector: 'app-create-sample-dialog',
    templateUrl: './create-sample-dialog.component.html',
    styleUrls: [ './create-sample-dialog.component.scss' ],
    imports: [
        DialogModule,
        InputTextModule,
        ButtonModule,
        FormsModule,
        IconField,
        InputIcon,
        IftaLabelModule,
        Select,
        InputNumber,
        MultiSelect,
        SelectOrUploadMediaComponent
    ]
})
export class CreateSampleDialogComponent implements OnInit, OnChanges {

    @Input() visible: boolean = false;
    @Input() toCopy: SampleWriteDTO | null = null;
    @Output() visibleChange: EventEmitter<boolean> = new EventEmitter<boolean>();
    @Output() sampleCreated: EventEmitter<any> = new EventEmitter<any>();

    public sample: SampleWriteDTO = new SampleWriteDTO({});
    public fieldOfUseOptions: SelectOption[] = [];
    public sampleTypeOptions: SampleType[] = [];
    public sampleFormatOptions: { label: string; value: SampleFormat }[] = [
        { label: 'Standard', value: SampleFormat.STANDARD },
        { label: 'G-Class Dedicated', value: SampleFormat.G_CLASS },
        { label: 'Maybach Dedicated', value: SampleFormat.MAYBACH },
    ];
    public sampleBrandingOptions: { label: string; value: SampleBranding }[] = [
        { label: 'Standard', value: SampleBranding.STANDARD },
        { label: 'Manufaktur', value: SampleBranding.MANUFAKTUR },
        { label: 'Manufaktur Exclusive', value: SampleBranding.MANUFAKTUR_EXCLUSIVE },
    ];

    public languages: Language[] = [];
    public manufacturers: Manufacturer[] = [];
    public media: Media | undefined = undefined;

    // Loading states
    public loading: boolean = false;
    public loadingLanguages: boolean = false;
    public loadingManufacturers: boolean = false;

    // Category options
    public categoryOptions: SampleCategory[] = [];
    public categoryDisabled: boolean = true;

    // Details
    public details: Detail[] = [];
    public selectedDetails: Detail[] = [];
    public detailsDisabled: boolean = true;

    // Misc
    public isFirstLoad: boolean = true;

    constructor(private utilsService: UtilsService,
                private languageService: LanguageService,
                private sampleService: SampleService,
                private messageService: MessageService,
                private manufacturerService: ManufacturerService,
                private sampleTypeService: SampleTypeService,
                private sampleCategoryService: SampleCategoryService,
                private detailsService: DetailService) {
    }

    public ngOnInit(): void {
        this.fieldOfUseOptions = this.utilsService.createDropdownOptions(FieldOfUse);

        // Load languages
        this.loadLanguages();

        // Load sample types
        this.loadSampleTypes();

        // Load manufacturers
        this.loadManufacturers();
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (changes['visible'] && changes['visible'].currentValue === true) {
            if (this.toCopy) {
                this.sample = this.toCopy;
                this.sample.id = undefined;
                if (this.sample?.typeId) {
                    this.loadSampleCategories(this.sample.typeId);
                }
            }
        }
    }

    public get saveDisabled(): boolean {
        return !this.sample.printedName || !this.sample.fieldOfUse || !this.sample.typeId || !this.sample.es2Key ||
            !this.sample.weight;
    }

    public addLanguage() {
        this.sample.sampleLanguages.push(new SampleLanguageWriteDTO({}));
    }

    public removeLanguage(index: number) {
        this.sample.sampleLanguages.splice(index, 1);
    }

    public close() {
        this.visible = false;
        this.toCopy = null;
        this.sample = new SampleWriteDTO({});
        this.isFirstLoad = true;
        this.visibleChange.emit(this.visible);
    }

    public saveSample() {
        this.loading = true;
        this.sample.categoryDetailIds = this.selectedDetails.map((detail) => detail.id);
        if (this.media) {
            this.sample.mediaId = this.media.id;
        }
        this.sampleService.createSample(this.sample).then(() => {
            this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Sample created', life: 3000 });
            this.sampleCreated.emit();
            this.sample = new SampleWriteDTO({});
            this.close();
        }).catch((error: any) => {
            this.messageService.add({ severity: 'error', summary: 'Error', detail: error.message, life: 6000 });
        }).finally(() => {
            this.loading = false;
        });
    }

    public getAvailableLanguages(index: number): Language[] {
        // Get the IDs of languages selected in other dropdowns
        const selectedLanguageIds = this.sample.sampleLanguages
            .filter((lang, idx) => idx !== index && lang.languageId != null)
            .map((lang) => lang.languageId);

        // Return languages that are not selected in other dropdowns
        return this.languages.filter(
            (language) =>
                !selectedLanguageIds.includes(language.id) ||
                language.id === this.sample.sampleLanguages[index].languageId
        );
    }

    public loadSampleTypes() {
        this.sampleTypeService.getAllSampleTypes().then((sampleTypes) => {
            this.sampleTypeOptions = sampleTypes;
        }).catch(() => {
            this.messageService.add(
                { severity: 'error', summary: 'Error', detail: 'Failed to fetch sample types', life: 3000 });
        });
    }

    public loadSampleCategories(sampleTypeId: number) {
        this.sampleCategoryService.getSampleCategoriesByType(sampleTypeId).then((categories) => {
            this.categoryOptions = categories;
            // Automatically set category if there's only one allowed option
            if (this.categoryOptions.length === 1) {
                this.sample.categoryId = this.categoryOptions[0].id;
                this.categoryDisabled = true;
            } else this.categoryDisabled = this.categoryOptions.length == 0;
        }).catch(() => {
            this.messageService.add(
                { severity: 'error', summary: 'Error', detail: 'Failed to fetch categories', life: 3000 });
        });
    }

    public loadManufacturers() {
        this.loadingManufacturers = true;
        this.manufacturerService.getAllManufacturers().then((manufacturers) => {
            this.manufacturers = manufacturers;
        }).catch(() => {
            this.messageService.add(
                { severity: 'error', summary: 'Error', detail: 'Failed to fetch manufacturers', life: 3000 });
        }).finally(() => {
            this.loadingManufacturers = false;
        });
    }

    private loadLanguages() {
        this.loadingLanguages = true;
        this.languageService.getAllLanguages().then((languages) => {
            this.languages = languages;
        }).catch(() => {
            this.messageService.add(
                { severity: 'error', summary: 'Error', detail: 'Failed to fetch languages', life: 3000 });
        }).finally(() => {
            this.loadingLanguages = false;
        });
    }

    public onSampleTypeChange(sampleTypeId: number): void {
        if (sampleTypeId && !( this.isFirstLoad && this.toCopy )) {
            // Reset category and load allowed categories for the selected type
            this.sample.categoryId = undefined;
            const selectedType = this.sampleTypeOptions.find((type) => type.id === sampleTypeId);
            if (selectedType?.defaultWeight) {
                this.sample.weight = selectedType.defaultWeight;
            }
            this.sampleCategoryService.getSampleCategoriesByType(sampleTypeId).then((categories) => {
                this.categoryOptions = categories;
                // Automatically set category if there's only one allowed option
                if (this.categoryOptions.length === 1) {
                    this.sample.categoryId = this.categoryOptions[0].id;
                    this.categoryDisabled = true;
                } else this.categoryDisabled = this.categoryOptions.length == 0;
            }).catch(() => {
                this.messageService.add(
                    { severity: 'error', summary: 'Error', detail: 'Failed to fetch categories', life: 3000 });
            });
        }
        this.isFirstLoad = false;
    }

    public onSampleCategoryChange(categoryId: number): void {
        if (categoryId) {
            const selectedCategory = this.categoryOptions.find((category) => category.id === categoryId);
            if (selectedCategory?.defaultWeight) {
                this.sample.weight = selectedCategory.defaultWeight;
            }
            this.loadDetails();
        }
    }

    public loadDetails(): void {
        this.detailsService.getDetailsByCategory(this.sample.categoryId!).then((details) => {
            this.details = details;
            this.detailsDisabled = this.details.length === 0;
        }).catch(() => {
            this.messageService.add(
                { severity: 'error', summary: 'Error', detail: 'Failed to fetch details', life: 3000 });
        });
    }

}
